import {
  isEcoFoot2Plus,
  isFlatRoofSlopeProduct,
  isRM10orRM10Evo,
  isRM5,
  isRmGridflex10D,
  isRmGridflex5Rwdi,
  isRMIFIProduct,
} from '__common/constants/products';
import { isASCE710 } from '__common/constants/buildingCodes';
import { degreesToRadians } from '__common/calculations/degreesToRadians';
import {
  getRealRoofEdgesBounds,
  getScaleFromPitchRoof,
  getSetBackDistanceBetweenPolygons,
  singlePanelPolygon,
} from '__editor/panelsEditor/geometry/setbackGeometry';

/**
 * The slope a corrupted flat roof project is stuck at.
 *
 * It was written by switchProjectToASCE716(), which raises any slope of 1-8 to
 * the ASCE 7-16 minimum of 9 for pitched (non flat) roofs. While a building code
 * switch was still forcing `building_type` to `gable` for flat roof products,
 * that raise also ran for them and overwrote the slope the user had selected.
 *
 * Most flat roof products cap their selector at 7 degrees (3 for EcoFoot2+), so
 * for those a stored 9 cannot have come from the user at all - see
 * hasUnconditionalSubNineSlopeCap().
 */
export const CORRUPTED_FLAT_ROOF_PITCH = 9;

const ECOFOOT2_PLUS_MAX_PITCH = 3;
const CALIFORNIA_ASCE_710_MAX_PITCH = 3;
const FLASHLOC_RM_MAX_PITCH = 5;
const FLAT_ROOF_MAX_PITCH = 7;
/** The top of RoofPitchSelector's unrestricted `roofPitchesDegrees` list. */
const ALL_DEGREES_MAX_PITCH = 45;

/** The range switchProjectToASCE716() raises to 9, so the range it can have overwritten. */
const MIN_CORRUPTIBLE_PITCH = 1;
const MAX_CORRUPTIBLE_PITCH = 8;

/**
 * The persisted setback is rounded to two decimal places, so a candidate that
 * reproduces the stored geometry can still be off by a rounding step on
 * individual panels. Averaged over the fitted panels the true slope stays well
 * under half a step; anything above that is not the slope the panels were laid
 * out at.
 */
const MAX_FIT_ERROR_FT = 0.005;

/**
 * Candidates this close to the winner are indistinguishable given the rounding,
 * so they get resolved by picking the steepest rather than by the fit.
 */
const TIE_BAND_FT = 0.001;

/**
 * The slope only rescales the roof polygon along Y, so panels furthest from the
 * roof centre in Y carry nearly all of the signal, and panels whose nearest edge
 * runs north-south carry none. Fitting against the former keeps project load
 * cheap on large arrays without giving up discrimination.
 */
const MAX_PANELS_PER_FIT = 32;

export interface RoofPitchLimits {
  buildingCode?: number;
  /** Project location, as `state().projectConfiguration.area` holds it. */
  area?: string;
  /** Whether UNIRAC FLASHLOC RM mechanical attachments are in use. */
  flashLocRmMechanicalAttachments?: boolean;
}

export const toPitchNumber = (roofPitch: any): number | null => {
  if (roofPitch === null || roofPitch === undefined || roofPitch === '' || roofPitch === '---') {
    return null;
  }
  const pitch = Number(roofPitch);
  return Number.isFinite(pitch) ? pitch : null;
};

export const hasCorruptedFlatRoofPitch = (productId: number, roofPitch: any): boolean =>
  isFlatRoofSlopeProduct(productId) && toPitchNumber(roofPitch) === CORRUPTED_FLAT_ROOF_PITCH;

const isCalifornia = (area: string): boolean => {
  const stateName = (area || '').split(',')[1];
  return stateName !== undefined && stateName.trim() === 'CA';
};

/**
 * Whether RoofPitchSelector caps this product below 9 degrees for *every*
 * building code, which is what makes a stored 9 provably corrupt.
 *
 * These are the products getRoofPitches() answers before it looks at
 * `building_code`, so they only ever see the 0-7 list (0-3 for EcoFoot2+).
 * RM DT and the legacy RM10 fall through those branches instead and get the
 * full 1-45 degree list under ASCE 7-10, so 9 is a slope a user really could
 * have picked for them and must not be overwritten on a hunch.
 */
export const hasUnconditionalSubNineSlopeCap = (productId: number): boolean =>
  isRMIFIProduct(productId) || isRM5(productId) || isRmGridflex10D(productId)
  || isRmGridflex5Rwdi(productId) || isRM10orRM10Evo(productId) || isEcoFoot2Plus(productId);

/**
 * Mirrors the option list RoofPitchSelector builds, so a recovered slope is
 * always one the user could have picked in the first place.
 */
export const maxSelectableRoofPitch = (productId: number, limits: RoofPitchLimits = {}): number => {
  // RM DT and the legacy RM10 miss the branches that cap the list, and get the
  // full degree list instead. Their attachment type does not narrow it either.
  if (!hasUnconditionalSubNineSlopeCap(productId)) {
    return ALL_DEGREES_MAX_PITCH;
  }
  if (isEcoFoot2Plus(productId)) {
    return ECOFOOT2_PLUS_MAX_PITCH;
  }
  if (isRMIFIProduct(productId) && isASCE710(limits.buildingCode) && isCalifornia(limits.area)) {
    return CALIFORNIA_ASCE_710_MAX_PITCH;
  }
  if (limits.flashLocRmMechanicalAttachments) {
    return FLASHLOC_RM_MAX_PITCH;
  }
  return FLAT_ROOF_MAX_PITCH;
};

/**
 * The slopes the corruption could have started from: what
 * switchProjectToASCE716() raises, narrowed to what this product's selector
 * offered.
 *
 * The raise only fires for a stored '1' through '8', so 0 and '---' are not
 * candidates however old the project is - a flat roof left at 0 was never
 * touched. The top comes from the selector, which is what keeps a recovered
 * slope to something the user could have picked.
 */
export const candidateRoofPitches = (productId: number, limits: RoofPitchLimits): number[] => {
  const max = Math.min(MAX_CORRUPTIBLE_PITCH, maxSelectableRoofPitch(productId, limits));
  const pitches: number[] = [];
  for (let pitch = MIN_CORRUPTIBLE_PITCH; pitch <= max; pitch++) {
    pitches.push(pitch);
  }
  return pitches;
};

const mean = (values: number[]): number => values.reduce((sum, value) => sum + value, 0) / values.length;

/**
 * Panels whose stored setback is missing or zero are unusable: parseSaveData
 * substitutes a product level fallback for those, which carries no geometry.
 */
const fittablePanels = (panels: panelInState[]): panelInState[] => {
  const usable = (panels || []).filter(panel =>
    Number.isFinite(panel.panelSetback) && panel.panelSetback > 0
    && Number.isFinite(panel.x) && Number.isFinite(panel.y)
    && Number.isFinite(panel.width) && Number.isFinite(panel.height));
  if (usable.length <= MAX_PANELS_PER_FIT) {
    return usable;
  }
  const centreY = mean(usable.map(panel => panel.y));
  return [...usable]
    .sort((a, b) => Math.abs(b.y - centreY) - Math.abs(a.y - centreY))
    .slice(0, MAX_PANELS_PER_FIT);
};

/**
 * Replays getPanelSetBackDistance() for one candidate slope.
 *
 * It builds the roof polygon directly rather than going through getRoofArea(),
 * because the repair runs while the project is still loading and getRoofArea()
 * reads a map type that setMapType() has not dispatched yet. Everything that
 * matters here comes off the saved roof instead.
 */
const setbacksForPitch = (roof: any, panels: panelInState[], productId: number, tilt: number, roofPitch: string): number[] => {
  const roofPolygon = getRealRoofEdgesBounds({
    roofEdges: roof.coords,
    roofCenter: roof.marker,
    bgOffSet: roof.bgOffset,
    zoom: roof.zoom,
    rotationRadians: degreesToRadians(roof.bgRotation),
    roofPitch,
    productId,
    tilt,
  });

  return panels.map((panel) => {
    const panelPoly = singlePanelPolygon(
      panel,
      true,
      roof.bgOffset.x,
      roof.bgOffset.y,
      panel.width,
      panel.height,
      roof.metersPerPixel,
    );
    return getSetBackDistanceBetweenPolygons(panelPoly, roofPolygon, roof.metersPerPixel);
  });
};

/**
 * The panels editor measures each panel's setback against the roof polygon
 * *after* it has been stretched along Y by getScaleFromPitchRoof(roofPitch), and
 * stores the result on the panel. Nothing in the ASCE 7-16 switch rewrites those
 * setbacks, so on a corrupted project they still describe the roof at the slope
 * the user actually selected, which makes them invertible.
 *
 * So instead of trusting the stored slope we replay the setback calculation for
 * every slope the corruption could have overwritten and keep the one that
 * reproduces the stored setbacks. Returns null when the geometry cannot single
 * one out:
 *  - the product ignores the slope when scaling (RM10, RM10 Evolution, GridFlex
 *    10D and GridFlex 5 RWDI all get a scale of 1),
 *  - the roof lacks the geometry the setbacks were measured against,
 *  - no panel has a usable stored setback,
 *  - no candidate reproduces the stored setbacks,
 *  - the corrupted slope itself fits at least as well, meaning the setbacks were
 *    regenerated after the corruption and carry no trace of the original slope,
 *  - every candidate fits equally well.
 */
export const backCalculateRoofPitchFromPanelSetbacks = (
  roof: any,
  productId: number,
  tilt: number,
  limits: RoofPitchLimits,
): number | null => {
  if (!roof || !roof.coords || !roof.coords.length || !roof.marker
    || !roof.bgOffset || !Number.isFinite(roof.bgOffset.x) || !Number.isFinite(roof.bgOffset.y)
    || !Number.isFinite(roof.zoom) || !Number.isFinite(roof.bgRotation)
    || !Number.isFinite(roof.metersPerPixel)) {
    return null;
  }

  const panels = fittablePanels(roof.panels);
  if (!panels.length) {
    return null;
  }

  const candidates = candidateRoofPitches(productId, limits);
  const scales = candidates.map(pitch => getScaleFromPitchRoof(String(pitch), productId));
  if (new Set(scales).size < 2) {
    return null;
  }

  const stored = panels.map(panel => panel.panelSetback);
  const fitError = (pitch: number): number => {
    const recomputed = setbacksForPitch(roof, panels, productId, tilt, String(pitch));
    if (recomputed.some(setback => !Number.isFinite(setback))) {
      return Infinity;
    }
    return mean(recomputed.map((setback, index) => Math.abs(setback - stored[index])));
  };

  const errors = candidates.map(fitError);
  const best = Math.min(...errors);
  if (!(best <= MAX_FIT_ERROR_FT)) {
    return null;
  }
  if (fitError(CORRUPTED_FLAT_ROOF_PITCH) <= best) {
    return null;
  }

  const tied = candidates.filter((_, index) => errors[index] <= best + TIE_BAND_FT);
  if (tied.length === candidates.length) {
    return null;
  }
  // Slope raises both the sliding and the uplift ballast factors, so the
  // steepest of the indistinguishable candidates is the safe side to err on.
  return Math.max(...tied);
};
